import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Badge,
  Popover,
  Typography,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  List,
  Divider,
  Button,
  Avatar,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import { PATH_DASHBOARD } from '../../../routes/paths';
import {
  PanelNotification,
  NOTIFICATION_EVENT_ICONS,
  getNotificationsApi,
  getUnreadCountApi,
  markAllNotificationsReadApi,
  markNotificationReadApi,
} from '../../../api/notificationApi';
import { enablePush, pushStatus, PushStatus } from '../../../utils/pushNotifications';
import { fToNow } from '../../../utils/formatTime';

// The bell shows the newest few; "View all" opens the full list (pages/notifications)
const BELL_LIMIT = 6;
const REFRESH_MS = 30_000;

export function NotificationIcon({ event }: { event: PanelNotification['event'] }) {
  const [icon, color] = NOTIFICATION_EVENT_ICONS[event] || ['eva:bell-fill', 'info.main'];
  return <Iconify icon={icon} sx={{ color, width: 22, height: 22 }} />;
}

export default function NotificationsPopover() {
  const navigate = useNavigate();
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<PanelNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [push, setPush] = useState<PushStatus | null>(null);
  const [pushBusy, setPushBusy] = useState(false);

  const loadCount = useCallback(() => {
    getUnreadCountApi()
      .then((res) => setUnreadCount(res?.unread_count ?? 0))
      .catch(() => undefined);
  }, []);

  const loadList = useCallback(() => {
    setLoading(true);
    getNotificationsApi({ limit: BELL_LIMIT })
      .then((res) => {
        setItems(res?.items || []);
        setUnreadCount(res?.unread_count ?? 0);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  // The badge: now, every REFRESH_MS, and when the tab comes back into view
  useEffect(() => {
    loadCount();
    const timer = window.setInterval(loadCount, REFRESH_MS);
    const onVisible = () => document.visibilityState === 'visible' && loadCount();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadCount]);

  // A new unread one while the bell is open shows at once
  useEffect(() => {
    if (open) loadList();
  }, [open, unreadCount, loadList]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
    if (push === null) pushStatus().then(setPush).catch(() => setPush('unsupported'));
  };

  const handleClose = () => setOpen(null);

  const handleClick = (item: PanelNotification) => {
    if (item.unread) {
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, unread: false } : x)));
      setUnreadCount((n) => Math.max(0, n - 1));
      markNotificationReadApi(item.id).catch(() => undefined);
    }
    handleClose();
    navigate(item.link);
  };

  const handleMarkAllRead = () => {
    setItems((prev) => prev.map((x) => ({ ...x, unread: false })));
    setUnreadCount(0);
    markAllNotificationsReadApi().catch(() => loadList());
  };

  const handleViewAll = () => {
    handleClose();
    navigate(PATH_DASHBOARD.notifications);
  };

  const handleEnablePush = () => {
    setPushBusy(true);
    enablePush()
      .then(setPush)
      .catch(() => setPush('off'))
      .finally(() => setPushBusy(false));
  };

  return (
    <>
      <IconButton color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1.5, ml: 0.75, width: 380, maxWidth: 'calc(100vw - 24px)' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {unreadCount ? `${unreadCount} unread` : 'All caught up'}
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton color="primary" onClick={handleMarkAllRead}>
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {push === 'off' && (
          <Box sx={{ px: 2.5, pb: 1.5 }}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              disabled={pushBusy}
              onClick={handleEnablePush}
              startIcon={<Iconify icon="eva:bell-outline" />}
            >
              Get these on this device
            </Button>
          </Box>
        )}
        {push === 'blocked' && (
          <Typography variant="caption" sx={{ display: 'block', px: 2.5, pb: 1.5, color: 'text.secondary' }}>
            Browser notifications are blocked for this site. Allow them in the browser&apos;s site settings.
          </Typography>
        )}

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' }, maxHeight: 440 }}>
          <List disablePadding>
            {loading && !items.length ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : items.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No notifications yet
                </Typography>
              </Box>
            ) : (
              items.map((item) => (
                <ListItemButton
                  key={item.id}
                  onClick={() => handleClick(item)}
                  sx={{ py: 1.5, px: 2.5, mt: '1px', ...(item.unread ? { bgcolor: 'action.selected' } : {}) }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'background.neutral' }}>
                      <NotificationIcon event={item.event} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2">
                        {item.title}
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', display: 'block' }}>
                          {item.body}
                        </Typography>
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{ mt: 0.5, display: 'flex', alignItems: 'center', color: 'text.disabled' }}
                      >
                        <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
                        {fToNow(item.occurred_at)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple onClick={handleViewAll}>
            View all
          </Button>
        </Box>
      </Popover>
    </>
  );
}
